<template>
  <div class="main-contents">
    <div class="page-header">
      <div class="row align-items-center">
        <div class="col-md-7">
          <h1 class="page-title">Send WhatsApp Message</h1>
        </div>
      </div>
    </div>

    <div class="main-body">
      <div class="container-fluid">
        <div class="row">
          <div class="col-md-8 offset-md-2">
            <div class="card">
              <div class="card-body">
                <!-- Connected Info Banner -->
                <div class="alert alert-info mb-4" role="alert">
                  <i class="fa fa-info-circle mr-2"></i>
                  <strong>WhatsApp Connection Active:</strong> You are using
                  <strong>{{ connectedNumber }}</strong> to send messages
                </div>

                <!-- Conversation Thread Section -->
                <div v-if="messages.length > 0" class="conversation-section mb-4 p-4 bg-light rounded" style="max-height: 400px; overflow-y: auto; border: 1px solid #ddd;">
                  <h6 class="mb-3 text-muted">
                    <i class="fa fa-comments mr-2"></i>Message History
                  </h6>
                  <div v-for="msg in messages" :key="msg.id" class="message-bubble mb-3">
                    <div v-if="msg.type === 'sent'" class="text-end">
                      <div class="d-inline-block p-3 rounded bg-primary text-white" style="max-width: 70%; text-align: left;">
                        <div class="small mb-1">
                          <strong>Sent to {{ msg.customerName }}</strong>
                        </div>
                        <div>{{ msg.text }}</div>
                        <div class="small mt-1" style="opacity: 0.8;">{{ msg.timestamp }}</div>
                      </div>
                    </div>
                    <div v-else class="text-start">
                      <div class="d-inline-block p-3 rounded bg-secondary text-white" style="max-width: 70%; text-align: left;">
                        <div class="small mb-1">
                          <strong>{{ msg.customerName }}</strong>
                        </div>
                        <div>{{ msg.text }}</div>
                        <div class="small mt-1" style="opacity: 0.8;">{{ msg.timestamp }}</div>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Form Section -->
                <form @submit.prevent="handleSubmit">
                  <!-- Customer Selection -->
                  <div class="form-group mb-4">
                    <label for="customerSelect" class="form-label fw-bold mb-2"
                      >Select Customer</label
                    >
                    <select
                      v-model="selectedCustomer"
                      id="customerSelect"
                      class="form-control form-control-lg"
                      required
                    >
                      <option value="">-- Choose a customer --</option>
                      <option v-for="customer in dummyCustomers" :key="customer.id" :value="customer.id">
                        {{ customer.name }} ({{ customer.phone }})
                      </option>
                    </select>
                    <small class="text-muted d-block mt-2"
                      >Select the customer you want to send a message to</small
                    >
                  </div>

                  <!-- Message Textarea -->
                  <div class="form-group mb-4">
                    <label for="messageTextarea" class="form-label fw-bold mb-2"
                      >Message</label
                    >
                    <textarea
                      v-model="message"
                      id="messageTextarea"
                      class="form-control"
                      rows="6"
                      placeholder="Type your message here..."
                      required
                    ></textarea>
                    <small class="text-muted d-block mt-2">
                      Character count: {{ message.length }} / 4096
                    </small>
                  </div>

                  <!-- Submit Button -->
                  <div class="button-section mb-3">
                    <button
                      type="submit"
                      class="btn btn-primary btn-lg"
                      :disabled="!selectedCustomer || !message.trim() || isSending"
                    >
                      <i class="fa fa-paper-plane mr-2"></i>
                      {{ isSending ? 'Sending...' : 'Send Message' }}
                    </button>
                  </div>

                  <!-- Help Text -->
                  <div class="alert alert-light border border-secondary mt-4" role="alert">
                    <h6 class="mb-2">
                      <i class="fa fa-lightbulb-o mr-2 text-warning"></i>Demo Information
                    </h6>
                    <p class="mb-0 small text-muted">
                      This is a demo interface showing how you would send WhatsApp messages through the Streamly
                      platform. Messages will be sent to the selected customer via the connected WhatsApp Business
                      Account.
                    </p>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'

export default {
  name: 'WhatsAppMessage',
  data() {
    return {
      selectedCustomer: '',
      message: '',
      isSending: false,
      connectedNumber: '+923465054043', // From demo data
      messages: [], // Conversation history
      messageId: 0, // For unique message IDs
      dummyCustomers: [
        {
          id: 1,
          name: 'Robert Johnson',
          phone: '+15593827456',
        },
        {
          id: 2,
          name: 'Jane Smith',
          phone: '+199765432100',
        },
        {
          id: 3,
          name: 'John Doe',
          phone: '+123456789012',
        },
      ],
    };
  },
  mounted() {
    this.checkConnection();
    window.showHideMainLoader(false);
  },
  methods: {
    checkConnection() {
      // Verify that Meta is connected
      try {
        const storedConnection = localStorage.getItem('metaConnection');
        if (!storedConnection) {
          this.$notify({
            type: 'warning',
            message: 'Please connect your Meta account first',
            duration: 3000,
          });
          this.$router.push('/meta-connect');
        }
      } catch (error) {
        console.error('Error checking connection:', error);
      }
    },

    async handleSubmit() {
      if (!this.selectedCustomer || !this.message.trim()) {
        this.$notify({
          type: 'warning',
          message: 'Please select a customer and enter a message',
        });
        return;
      }

      this.isSending = true;
      const messageText = this.message;
      const customerId = this.selectedCustomer;
      const customerName = this.getSelectedCustomerName();

      try {
        // Call the backend API using axios
        const response = await axios.get('meta/whatsapp/send_message', {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('auth_token') || ''}`,
          }
        });

        const data = response.data;

        if (data.status === 'success' || data.success) {
          // Add message to conversation
          this.messageId++;
          this.messages.push({
            id: this.messageId,
            type: 'sent',
            text: messageText,
            customerName: customerName,
            customerId: customerId,
            timestamp: this.getCurrentTimestamp(),
          });

          // Show success notification
          window.toast.fire({
            icon: 'success',
            title: 'Message Sent!',
            text: `Your message has been sent to ${customerName}`,
          });

          // Reset form
          this.selectedCustomer = '';
          this.message = '';

          // Scroll to bottom of conversation
          this.$nextTick(() => {
            const conversationDiv = document.querySelector('.conversation-section');
            if (conversationDiv) {
              conversationDiv.scrollTop = conversationDiv.scrollHeight;
            }
          });
        } else {
          throw new Error(data.message || 'Failed to send message');
        }
      } catch (error) {
        console.error('Error sending message:', error);
        window.toast.fire({
          icon: 'error',
          title: 'Failed to Send',
          text: error.message || 'An error occurred while sending your message. Please try again.',
        });
      } finally {
        this.isSending = false;
      }
    },

    simulateSendMessage() {
      // Simulate a 1.5 second API delay
      return new Promise((resolve) => {
        setTimeout(() => {
          resolve();
        }, 1500);
      });
    },

    getCurrentTimestamp() {
      const now = new Date();
      return now.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      });
    },

    getSelectedCustomerName() {
      const customer = this.dummyCustomers.find((c) => c.id === parseInt(this.selectedCustomer));
      return customer ? customer.name : 'customer';
    },
  },
};
</script>

<style scoped>
.main-body {
  padding: 30px 0;
}

.form-label {
  color: #333;
  font-size: 0.95rem;
}

.form-control,
.form-control-lg {
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 1rem;
}

.form-control:focus {
  border-color: #007bff;
  box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
}

textarea.form-control {
  font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
  line-height: 1.5;
  resize: vertical;
}

.button-section {
  display: flex;
  gap: 10px;
}

.btn:disabled {
  cursor: not-allowed;
  opacity: 0.6;
}

.alert {
  border-radius: 6px;
}

.alert-info {
  background-color: #e7f3ff;
  border-color: #0066cc;
}

.text-warning {
  color: #ffc107;
}

small {
  display: block;
  margin-top: 0.25rem;
}
</style>
